/**
 * Merchant notes — threaded CRM timeline.
 *
 * Replaces the one-shot "Quick share" textarea. Each AM can append
 * notes; notes are append-only by design (no inline edit) but the
 * author can delete their own notes within 24h, and super_admins can
 * delete anyone's. Backend keeps the trail in `merchant_notes` and
 * audit-logs every create/delete.
 *
 * Sorted newest-first. Optimistic UX on create (note appears
 * immediately, errors revert).
 */
import React, { useEffect, useState } from "react";
import { Loader2, Send, Trash2 } from "lucide-react";
import api from "../../../lib/api";

const MAX_LEN = 4000;

function formatStamp(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function MerchantNotes({ merchantId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/merchants/${merchantId}/notes`);
      setNotes(data.notes || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load notes.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/merchants/${merchantId}/notes`);
        if (!alive) return;
        setNotes(data.notes || []);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Couldn't load notes.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [merchantId]);

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/notes`, { body: trimmed },
      );
      setNotes((prev) => [data.note, ...prev]);
      setDraft("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save note.");
    } finally { setBusy(false); }
  };

  const deleteNote = async (note_id) => {
    if (!window.confirm("Delete this note? Audit-logged.")) return;
    try {
      await api.delete(`/admin/merchants/${merchantId}/notes/${note_id}`);
      setNotes((prev) => prev.filter((n) => n.note_id !== note_id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  const onKey = (e) => {
    // Cmd/Ctrl+Enter to submit — power-user shortcut.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (!busy && draft.trim()) submit();
    }
  };

  const remaining = MAX_LEN - draft.length;

  return (
    <section
      data-testid="merchant-notes"
      className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5 sm:p-6"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Notes</div>
        <div className="text-[10.5px] text-zinc-600">
          {loading ? "Loading…" : `${notes.length} ${notes.length === 1 ? "entry" : "entries"}`}
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-white/[0.06] bg-black/30">
        <textarea
          data-testid="merchant-notes-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={onKey}
          placeholder="Add a note about this merchant — email follow-ups, calls, anything an AM should know. Cmd+Enter to send."
          rows={3}
          disabled={busy}
          className="w-full bg-transparent text-[13px] text-white placeholder:text-zinc-600 px-3.5 py-3 focus:outline-none resize-none"
        />
        <div className="flex items-center justify-between px-3.5 py-2 border-t border-white/[0.04]">
          <span className={`text-[10.5px] ${remaining < 100 ? "text-amber-300" : "text-zinc-600"}`}>
            {draft.length}/{MAX_LEN}
          </span>
          <button
            type="button"
            data-testid="merchant-notes-submit"
            onClick={submit}
            disabled={busy || !draft.trim()}
            className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/85 hover:bg-cyan-500 text-black px-4 py-1.5 text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Add note
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="merchant-notes-error" className="mt-2 text-[11.5px] text-rose-300">{error}</div>
      )}

      <div className="mt-5 space-y-3">
        {!loading && notes.length === 0 && (
          <div className="text-[12.5px] text-zinc-500 py-3">
            No notes yet. The first AM to chat with this merchant should drop a line here.
          </div>
        )}
        {notes.map((n) => (
          <article
            key={n.note_id}
            data-testid={`merchant-note-${n.note_id}`}
            className="group rounded-xl border border-white/[0.05] bg-white/[0.015] px-4 py-3"
          >
            <header className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-medium text-white">{n.author_name || n.author_email}</span>
                <span className="text-[10.5px] text-zinc-600">{formatStamp(n.created_at)}</span>
              </div>
              {n.can_delete && (
                <button
                  type="button"
                  onClick={() => deleteNote(n.note_id)}
                  data-testid={`merchant-note-delete-${n.note_id}`}
                  title="Delete this note (audit-logged)"
                  className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 rounded-full text-[10.5px] text-rose-300/80 hover:text-rose-200 px-2 py-1"
                >
                  <Trash2 className="h-3 w-3" /> delete
                </button>
              )}
            </header>
            <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-200 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {n.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
